import axios from "axios";
import qs from "qs";

type TrueFalse = "true" | "false";

export type CoordinatePointArray = [number, number]; // lng, lat

type MapboxProfiles = "mapbox/driving-traffic" | "mapbox/driving" | "mapbox/walking" | "mapbox/cycling";

interface Options {
  api_key: string;
}

export interface MapboxGeocodeAutocompleteOptions {
  autocomplete?: TrueFalse;
  bbox?: string; // "-123.55,23.11,111.23,44.53"
  country?: string; // "au"
  fuzzyMatch?: TrueFalse;
  language?: string; // "en"
  limit?: number; // 5-10
  proximity?: string; // "-123.55,23.11"
  routing?: TrueFalse;
  types?: string; // country, region, postcode, district, place, locality, neighborhood, address, and poi
}

export interface MapboxGeocodeAutocompleteResultFeature {
  id: string;
  text: string;
  type: "Feature";
  address: string;
  context: Array<{ id: string; language: string; text: string; wikidata: string }>;
  place_name: string;
  place_type: string[];
  center: [number, number];
  geometry: {
    type: "Point",
    coordinates: [number, number];
  };
  properties: {
    accuracy: string;
  };
  relevance: number;
}

export interface MapboxGeocodeAutocompleteResult {
  type: "FeatureCollection";
  query: string[];
  attribution: string;
  features: MapboxGeocodeAutocompleteResultFeature[];
}

export interface MapboxDirectionsOptions {
  alternatives?: TrueFalse;
  annotations?: string; // Array<"duration" | "speed" | "distance" | "congestion">;
  continue_straight?: TrueFalse;
  exclude?: string; // Array<"toll" | "motorway" | "ferry">;
  overview?: "full" | "simplified" | "false";
  radiuses?: number | "unlimited";
  approaches?: string;
  steps?: TrueFalse;
  banner_instructions?: TrueFalse;
  language?: string;
  roundabout_exits?: TrueFalse;
  voice_instructions?: TrueFalse;
  voice_units?: "imperial" | "metric";
  waypoint_names?: string;
}

export interface MapboxDirectionsWaypoint {
  name: string;
  location: [number, number];
  distance?: number;
}

export interface MapboxDirectionsRoute {
  duration: number;
  distance: number;
  weight: number;
  weight_name: "routability";
  geometry?: string;
  legs: ObjectAny[];
  voiceLocale?: string;
}

export interface MapboxDirectionsResult {
  code: "Ok" | "NoRoute" | "NoSegment" | "ProfileNotFound" | "InvalidInput";
  waypoints: MapboxDirectionsWaypoint[];
  routes: MapboxDirectionsRoute[];
}

export class MapboxAPIClass {

  opts: Options;

  constructor(opts: Options) {

    this.opts = opts;

  }

  request = async (opts: { method: string; path: string; data?: ObjectAny; }) => {

    try {
      const response = await axios({
        url: `https://api.mapbox.com${opts.path}`,
        method: opts.method || "get",
        data: opts.data || {},
      });
      return response.data;
    }
    catch (e) {
      if (e.response && e.response.data) {
        throw e.response.data;
      }
      else {
        throw e;
      }
    }

  }

  directions = async (profile: MapboxProfiles, from: CoordinatePointArray, to: CoordinatePointArray, opts?: MapboxDirectionsOptions): Promise<MapboxDirectionsResult> => {
    const query = qs.stringify({
      access_token: this.opts.api_key,
      ...(opts || {}),
    });

    const coordinates = `${from[0]},${from[1]};${to[0]},${to[1]}`;

    const path = `/directions/v5/${profile}/${coordinates}?${query}`;

    return this.request({ method: "get", path: path });
  }

  geocode_autocomplete = async (search: string, opts?: MapboxGeocodeAutocompleteOptions): Promise<MapboxGeocodeAutocompleteResult> => {
    const query = qs.stringify({
      access_token: this.opts.api_key,
      ...(opts || {}),
    });

    const searchText = encodeURI(search);

    return this.request({
      method: "get",
      path: `/geocoding/v5/mapbox.places/${searchText}.json?${query}`,
    });
  }

}
